import * as THREE from 'three';
import { ShipPool } from '../pools/ShipPool';
import { OrbitController } from './OrbitController';
import { TrajectoryManager } from './TrajectoryManager';
import { DockingManager } from './DockingManager';
import { ShipFactory } from './ShipFactory';
import { TransactionShip, TransactionData } from './TransactionShip';
import { BlockData } from './BlockPlanet';
import { SpawnPositioning } from '../utils/SpawnPositioning';
import { useGalaxyStore } from '../../core/state/GalaxyStore';
import { eventBus } from '../../core/state/EventBus';
import { visualizationEventBus } from '../events/VisualizationEventBus';
import { logger } from '../../core/utils/Logger';
import type { Transaction } from '../../core/models/Transaction';
import type { Block } from '../../core/models/Block';

export interface DynamicObjectsConfig {
  maxShips: number;
  maxPlanets: number;
  enableTrails: boolean;
  enableDocking: boolean;
  qualityLevel: 'low' | 'medium' | 'high' | 'ultra';
}

import { MiningOperationsManager, MiningOperationsConfig } from './MiningOperationsManager';

export class DynamicObjectsManager extends THREE.Group {
  private config: DynamicObjectsConfig;
  private shipPool: ShipPool;
  private orbitController: OrbitController;
  private trajectoryManager: TrajectoryManager;
  private dockingManager: DockingManager;
  
  // Phase 3: Mining operations for transaction selection
  private miningOperations: MiningOperationsManager | null = null;
  
  // Track active ships by transaction ID
  private activeShips: Map<string, TransactionShip> = new Map();
  
  // Event handlers
  private eventHandlers: Map<string, (...args: any[]) => void> = new Map();
  
  // Performance tracking
  private lastUpdateTime: number = 0;
  private updateDeltaAccumulator: number = 0;
  
  constructor(scene: THREE.Scene, config?: Partial<DynamicObjectsConfig>) {
    super();
    
    logger.debug('Constructor called', 'DynamicObjectsManager');
    
    this.config = {
      maxShips: 1000,
      maxPlanets: 10,
      enableTrails: true,
      enableDocking: true,
      qualityLevel: 'high',
      ...config
    };
    
    logger.debug(`Config: maxShips=${this.config.maxShips}, quality=${this.config.qualityLevel}`, 'DynamicObjectsManager');
    
    // Initialize systems
    this.shipPool = new ShipPool(scene, 100, this.config.maxShips);
    this.orbitController = new OrbitController(scene, this.config.maxPlanets);
    this.trajectoryManager = new TrajectoryManager();
    this.dockingManager = new DockingManager(this.shipPool, this.orbitController);
    
    // Phase 3: Initialize mining operations
    this.initializeMiningOperations(scene);
    
    logger.debug('Systems initialized', 'DynamicObjectsManager');
    
    // Setup event listeners
    this.setupEventListeners();
    this.connectToStore();
    
    this.userData = { id: 'dynamic-objects-manager', type: 'manager' };
    
    logger.info('Fully initialized', 'DynamicObjectsManager');
  }
  
  private setupEventListeners(): void {
    logger.debug('Setting up EventBus listeners', 'DynamicObjectsManager');
    
    // Only listen to VisualizationEventBus to avoid duplicate processing
    // The flow is: EventBus → GalaxyStore → useGalaxyScene → VisualizationEventBus → here
    
    // Transaction events for spawning ships
    const visualTransactionHandler = (data: any) => {
      logger.debug('Received VisualizationEventBus transaction', 'DynamicObjectsManager');
      this.handleTransactionEvent(data);
    };
    visualizationEventBus.on('transaction', visualTransactionHandler);
    this.eventHandlers.set('vis_transaction', visualTransactionHandler);
    
    // Block events for creating planets
    const visualBlockHandler = (data: any) => {
      logger.debug('Received VisualizationEventBus block', 'DynamicObjectsManager');
      this.handleBlockEvent(data);
    };
    visualizationEventBus.on('block', visualBlockHandler);
    this.eventHandlers.set('vis_block', visualBlockHandler);
    
    logger.debug('EventBus listeners registered', 'DynamicObjectsManager');
  }
  
  private connectToStore(): void {
    logger.debug('Connecting to store for initial data', 'DynamicObjectsManager');
    
    // Load any existing data from store on initialization
    this.loadExistingData();
    
    logger.debug('Store connection complete', 'DynamicObjectsManager');
  }
  
  private loadExistingData(): void {
    const store = useGalaxyStore.getState();
    
    logger.info(`Loading ${store.blockchain.blocks.size} blocks and ${store.blockchain.transactions.size} transactions from store`, 'DynamicObjectsManager');
    
    // Load existing blocks as planets
    const blocks = Array.from(store.blockchain.blocks.values())
      .sort((a, b) => b.height - a.height)
      .slice(0, this.config.maxPlanets);
    
    blocks.forEach(block => {
      logger.debug(`Creating planet for block ${block.height}`, 'DynamicObjectsManager');
      this.createPlanetFromBlock(block);
    });
    
    // Load existing transactions as ships
    const transactions = Array.from(store.blockchain.transactions.values())
      .filter(tx => tx.status === 'pending');
    
    transactions.forEach(tx => {
      logger.debug(`Creating ship for transaction ${tx.id}`, 'DynamicObjectsManager');
      this.createShipFromTransaction(tx);
    });
    
    logger.info(`Loaded ${blocks.length} planets and ${transactions.length} ships from store`, 'DynamicObjectsManager');
    
    // If no data in store yet, create initial test data to provide visual feedback
    if (blocks.length === 0 && transactions.length === 0) {
      logger.info('No data in store yet, creating initial test data for visualization', 'DynamicObjectsManager');
      this.createTestPlanets();
      this.createTestShips();
    }
  }
  
  private createTestPlanets(): void {
    const currentHeight = 850000;
    for (let i = 0; i < 5; i++) {
      // Generate a realistic 64-character hash for test blocks
      const testHash = this.generateTestHash(i);
      
      const testBlock: BlockData = {
        height: currentHeight + i,
        hash: testHash,
        timestamp: Date.now() - (i * 600000), // 10 minutes apart
        transactionCount: 1000 + Math.random() * 2000,
        totalFees: 50000000 + Math.random() * 100000000,
        miner: `TestPool${i}`,
        size: 1000000 + Math.random() * 500000
      };
      
      logger.debug(`Creating test planet for block ${testBlock.height}`, 'DynamicObjectsManager');
      const planet = this.orbitController.addBlock(testBlock);
      if (!planet) {
        logger.error(`Failed to create test planet for block ${testBlock.height}`, 'DynamicObjectsManager');
      } else {
        // Ensure the planet is properly positioned before any ships try to use it
        planet.update(0); // Force orbital position update
        const position = planet.getPosition();
        logger.debug(`Test planet ${testBlock.height} positioned at (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})`, 'DynamicObjectsManager');
      }
    }
  }

  private generateTestHash(index: number): string {
    // Generate a realistic 64-character hexadecimal hash for test blocks
    const chars = '0123456789abcdef';
    let hash = '';
    
    // Use index and timestamp for deterministic but varied hashes
    const seed = index * 12345 + Date.now();
    
    for (let i = 0; i < 64; i++) {
      const charIndex = (seed * (i + 1) * 7919) % chars.length;
      hash += chars[Math.floor(Math.abs(charIndex))];
    }
    
    return hash;
  }
  
  private clearTestObjects(): void {
    // Clear test planets (those with height >= 850000)
    const testPlanetHeights: number[] = [];
    this.orbitController.getAllPlanets().forEach(planet => {
      if (planet.blockHeight >= 850000) {
        testPlanetHeights.push(planet.blockHeight);
      }
    });
    
    testPlanetHeights.forEach(height => {
      this.orbitController.removePlanet(height);
    });
    
    // Clear test ships (those with IDs starting with "test_tx_")
    const testShipIds: string[] = [];
    for (const [txId, ship] of this.activeShips) {
      if (txId.startsWith('test_tx_')) {
        testShipIds.push(txId);
      }
    }
    
    testShipIds.forEach(txId => {
      this.releaseShip(txId);
    });
    
    logger.debug(`Cleared ${testPlanetHeights.length} test planets and ${testShipIds.length} test ships`, 'DynamicObjectsManager');
  }

  /**
   * Phase 3: Initialize mining operations with major mining pools
   */
  private initializeMiningOperations(_scene: THREE.Scene): void {
    const miningConfig: MiningOperationsConfig = {
      maxCollectors: 6,
      miningPools: [
        {
          name: 'AntPool',
          hashrate: 180000000000000, // 180 EH/s
          color: 0xff4444,
          feePreference: 'high'
        },
        {
          name: 'Foundry USA',
          hashrate: 150000000000000, // 150 EH/s
          color: 0x44ff44,
          feePreference: 'high'
        },
        {
          name: 'F2Pool',
          hashrate: 120000000000000, // 120 EH/s
          color: 0x4444ff,
          feePreference: 'medium'
        },
        {
          name: 'Binance Pool',
          hashrate: 100000000000000, // 100 EH/s
          color: 0xffaa44,
          feePreference: 'medium'
        },
        {
          name: 'ViaBTC',
          hashrate: 80000000000000, // 80 EH/s
          color: 0xaa44ff,
          feePreference: 'any'
        },
        {
          name: 'Poolin',
          hashrate: 60000000000000, // 60 EH/s
          color: 0x44aaff,
          feePreference: 'low'
        }
      ],
      competitionIntensity: 0.8,
      extractionRange: 50
    };
    
    console.log('🏭 Initializing Phase 3 Mining Operations...', miningConfig);
    // Pass this DynamicObjectsManager group instead of scene directly to ensure proper hierarchy
    this.miningOperations = new MiningOperationsManager(this, miningConfig);
    console.log('✅ Mining operations initialized with 6 collectors');
    console.log('🎯 Mining group added to DynamicObjectsManager:', this.miningOperations.getMiningOperationsStatus());
  }

  private createTestShips(): void {
    for (let i = 0; i < 10; i++) {
      const testTransaction: TransactionData = {
        amount: 100000 + Math.random() * 1000000, // 0.001 to 0.01 BTC
        fee: 1000 + Math.random() * 5000,
        feeRate: 5 + Math.random() * 50,
        size: 200 + Math.random() * 300
      };
      
      const transactionId = `test_tx_${i}_${Date.now()}`;
      logger.debug(`Creating test ship for transaction ${transactionId}`, 'DynamicObjectsManager');
      const ship = this.spawnShip(transactionId, testTransaction);
      
      if (!ship) {
        logger.error(`Failed to create test ship for transaction ${transactionId}`, 'DynamicObjectsManager');
        continue;
      }

      // Phase 1: Give test ships a trajectory from outer edge to mempool
      const origin = this.getOuterEdgeSpawnPosition();
      const destination = this.getMempoolTargetPosition(testTransaction.feeRate);
      
      // Validate positions before creating trajectory
      if (!origin || !destination) {
        logger.warn(`Invalid positions for test ship ${transactionId}: origin=${origin}, destination=${destination}`, 'DynamicObjectsManager');
        this.releaseShip(transactionId);
        continue;
      }
      
      // Check for invalid coordinates using SpawnPositioning validation
      if (!SpawnPositioning.isValidPosition(origin) || !SpawnPositioning.isValidPosition(destination)) {
        logger.warn(`Invalid coordinates for test ship ${transactionId}: origin=(${origin.x}, ${origin.y}, ${origin.z}), destination=(${destination.x}, ${destination.y}, ${destination.z})`, 'DynamicObjectsManager');
        this.releaseShip(transactionId);
        continue;
      }
      
      // Phase 1: Use enhanced trajectory path
      const path = SpawnPositioning.createTrajectoryPath(
        origin,
        destination,
        ship.shipClass
      );
      
      // Validate path before setting
      const pathLength = path.getLength();
      if (pathLength <= 0) {
        logger.warn(`Invalid path length (${pathLength}) for test ship ${transactionId}, skipping trajectory`, 'DynamicObjectsManager');
        this.releaseShip(transactionId);
        continue;
      }
      
      const distance = origin.distanceTo(destination);
      ship.setTrajectory(origin, destination, path);
      logger.debug(`Test ship ${transactionId} trajectory set: distance=${distance.toFixed(2)}, pathLength=${pathLength.toFixed(2)}`, 'DynamicObjectsManager');
    }
  }
  
  
  private handleTransactionEvent(data: Transaction | any): void {
    logger.debug(`Transaction event for ${data.id || 'unknown'}`, 'DynamicObjectsManager');
    
    // Convert data to transaction format if needed
    const transaction: TransactionData = {
      amount: data.value || data.amount || 0,
      fee: data.fee || 0,
      feeRate: data.feeRate || 10,
      size: data.size || 250
    };
    
    const transactionId = data.id || data.txid || `tx_${Date.now()}`;
    
    logger.debug(`Processing transaction ${transactionId} (${transaction.amount} sats)`, 'DynamicObjectsManager');
    
    // Don't create duplicate ships
    if (this.activeShips.has(transactionId)) {
      logger.debug(`Duplicate transaction skipped: ${transactionId}`, 'DynamicObjectsManager');
      return;
    }
    
    // Create ship
    const ship = this.spawnShip(transactionId, transaction);
    if (!ship) {
      logger.error(`Failed to create ship for transaction ${transactionId}`, 'DynamicObjectsManager');
      return;
    }

    // Find destination (latest block planet or mempool position)
    const latestPlanets = this.orbitController.getAllPlanets();
    if (latestPlanets.length > 0) {
      // Target the newest block
      const targetPlanet = latestPlanets[latestPlanets.length - 1];
      
      // CRITICAL FIX: Force planet position update and validate immediately
      targetPlanet.update(0);
      
      // Phase 1: Set trajectory from outer edge to mempool nebula
      const origin = this.getOuterEdgeSpawnPosition();
      const destination = this.getMempoolTargetPosition(transaction.feeRate);
      
      // Enhanced validation with specific error logging for debugging
      if (!origin || !destination) {
        logger.warn(`Invalid positions for ship ${transactionId}: origin=${origin}, destination=${destination}`, 'DynamicObjectsManager');
        this.releaseShip(transactionId);
        return;
      }
      
      // Phase 1: Check for invalid coordinates using enhanced validation
      if (!SpawnPositioning.isValidPosition(origin) || !SpawnPositioning.isValidPosition(destination)) {
        logger.warn(`Invalid coordinates for ship ${transactionId}: origin=(${origin.x}, ${origin.y}, ${origin.z}), destination=(${destination.x}, ${destination.y}, ${destination.z})`, 'DynamicObjectsManager');
        
        // ENHANCED FIX: Try alternative destination if planet position is invalid
        if (!SpawnPositioning.isValidPosition(destination)) {
          logger.warn(`Planet ${targetPlanet.blockHeight} has invalid position, forcing orbital update`, 'DynamicObjectsManager');
          // Force another orbital update and check again
          targetPlanet.setOrbit(30 + Math.random() * 10, Math.random() * Math.PI * 2, 0.1);
          const newDestination = targetPlanet.getPosition();
          
          if (SpawnPositioning.isValidPosition(newDestination)) {
            logger.info(`Fixed planet position for ship ${transactionId}`, 'DynamicObjectsManager');
            destination.copy(newDestination);
          } else {
            logger.error(`Could not fix planet position for ship ${transactionId}, releasing ship`, 'DynamicObjectsManager');
            this.releaseShip(transactionId);
            return;
          }
        } else {
          this.releaseShip(transactionId);
          return;
        }
      }
      
      // Check if positions are too similar
      const distance = origin.distanceTo(destination);
      if (distance < 1) {
        logger.warn(`Origin and destination too close for ship ${transactionId}: distance=${distance.toFixed(3)}`, 'DynamicObjectsManager');
        // Adjust destination to ensure minimum distance
        const direction = new THREE.Vector3().subVectors(destination, origin).normalize();
        if (direction.length() < 0.1) {
          direction.set(1, 0, 0); // Default direction if positions are identical
        }
        destination.add(direction.multiplyScalar(5)); // Ensure at least 5 units distance
      }
      
      // Phase 1: Use enhanced trajectory with ship class and fee rate
      const path = SpawnPositioning.createTrajectoryPath(
        origin,
        destination,
        ship.shipClass
      );
      
      // Validate path before setting
      const pathLength = path.getLength();
      if (pathLength <= 0) {
        logger.warn(`Invalid path length (${pathLength}) for ship ${transactionId}, skipping trajectory`, 'DynamicObjectsManager');
        logger.warn(`Path details: points=${path.points?.length}, origin=(${origin.x}, ${origin.y}, ${origin.z}), destination=(${destination.x}, ${destination.y}, ${destination.z})`, 'DynamicObjectsManager');
        this.releaseShip(transactionId);
        return;
      }
      
      ship.setTrajectory(origin, destination, path);
      
      // Enable docking for this ship
      if (this.config.enableDocking) {
        // Will dock when close enough
        ship.data.size = targetPlanet.blockHeight; // Store target block height
      }
      
      logger.debug(`Ship ${transactionId} trajectory set: distance=${distance.toFixed(2)}, pathLength=${pathLength.toFixed(2)}`, 'DynamicObjectsManager');
    } else {
      logger.warn(`No planets available for ship ${transactionId}, releasing ship`, 'DynamicObjectsManager');
      this.releaseShip(transactionId);
    }
  }
  
  private handleBlockEvent(data: Block | any): void {
    logger.info(`Block event for height ${data.height}`, 'DynamicObjectsManager');
    
    const blockData: BlockData = {
      height: data.height,
      hash: data.hash || data.id,
      timestamp: data.timestamp || Date.now(),
      transactionCount: data.transactionCount || data.txCount || 100,
      totalFees: data.totalFees || data.fees || 0,
      miner: data.minerPool || 'Unknown',
      size: data.size || 1000000
    };
    
    logger.debug(`Processing block ${blockData.height}`, 'DynamicObjectsManager');
    
    // Create new planet
    const planet = this.orbitController.addBlock(blockData);
    if (!planet) {
      logger.error(`Failed to create planet for block ${blockData.height}`, 'DynamicObjectsManager');
      return;
    }

    // Ensure the planet is properly positioned immediately
    planet.update(0); // Force orbital position update
    const position = planet.getPosition();
    logger.debug(`New planet ${blockData.height} positioned at (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})`, 'DynamicObjectsManager');
    
    // Move ships targeting this block to dock with the planet
    this.redirectShipsToPlanet(blockData.height);
    
    // Update store
    const store = useGalaxyStore.getState();
    store.registerPlanetPosition(blockData.height, {
      x: position.x,
      y: position.y,
      z: position.z
    });
  }
  
  private createPlanetFromBlock(block: Block): void {
    const blockData: BlockData = {
      height: block.height,
      hash: block.hash,
      timestamp: block.timestamp,
      transactionCount: block.transactionCount,
      totalFees: block.totalFees,
      miner: block.minerPool || 'Unknown',
      size: block.size
    };
    
    const planet = this.orbitController.addBlock(blockData);
    if (planet) {
      // Ensure the planet is properly positioned immediately
      planet.update(0); // Force orbital position update
      const position = planet.getPosition();
      logger.debug(`Planet from store ${blockData.height} positioned at (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})`, 'DynamicObjectsManager');
    }
  }
  
  private createShipFromTransaction(tx: Transaction): void {
    const transactionData: TransactionData = {
      amount: tx.amount,
      fee: tx.fee,
      feeRate: tx.feeRate,
      size: tx.size
    };
    
    this.spawnShip(tx.id, transactionData);
  }
  
  private spawnShip(transactionId: string, transaction: TransactionData): TransactionShip | null {
    try {
      // Check ship limit
      if (this.activeShips.size >= this.config.maxShips) {
        this.removeOldestShip();
      }
      
      // Get ship from pool
      const ship = this.shipPool.acquire();
      
      // Phase 1: Enhanced classification based on fee rate
      const shipClass = SpawnPositioning.classifyShipByFeeRate(transaction.feeRate);
      ship.activate(transaction, shipClass);
      
      // Phase 1: Set initial position at outer edge (will be updated by trajectory)
      const spawnPosition = this.getOuterEdgeSpawnPosition();
      ship.mesh.position.copy(spawnPosition);
      
      // Phase 1: Set engine glow based on fee urgency
      const glowIntensity = SpawnPositioning.getEngineGlowIntensity(transaction.feeRate);
      ship.engineGlow.setIntensity(glowIntensity);
      
      // Configure trails based on quality
      if (!this.config.enableTrails || this.config.qualityLevel === 'low') {
        ship.trail.mesh.visible = false;
      }
      
      // Set userData for interaction system with ship- prefix to match store expectations
      ship.mesh.userData = {
        id: `ship-${transactionId}`,
        type: 'ship',
        interactive: true,
        transactionData: transaction,
        shipClass: shipClass
      };
      
      // Track active ship locally
      this.activeShips.set(transactionId, ship);
      
      // Note: The store's activeShips Set is managed by the addTransaction action
      // in the blockchain slice, so we don't directly mutate it here
      
      return ship;
    } catch (error) {
      logger.error(`Failed to spawn ship for ${transactionId}`, 'DynamicObjectsManager', error);
      return null;
    }
  }
  
  private removeOldestShip(): void {
    if (this.activeShips.size === 0) return;
    
    // Find oldest ship
    const [oldestId, oldestShip] = this.activeShips.entries().next().value;
    
    // Release ship
    this.releaseShip(oldestId);
  }
  
  private releaseShip(transactionId: string): void {
    try {
      const ship = this.activeShips.get(transactionId);
      if (!ship) return;
      
      // Cancel any docking
      this.dockingManager.cancelDocking(ship.id);
      
      // Release to pool
      this.shipPool.release(ship);
      
      // Remove from local tracking
      this.activeShips.delete(transactionId);
      
      // Note: The store's activeShips Set is managed by the removeTransaction action
      // in the blockchain slice, so we don't directly mutate it here
    } catch (error) {
      logger.error(`Failed to release ship ${transactionId}`, 'DynamicObjectsManager', error);
    }
  }
  
  private getMempoolPosition(): THREE.Vector3 {
    // Phase 1: Return mempool nebula center as target destination
    // Ships will travel FROM outer edge TO this position
    return new THREE.Vector3(0, 0, 0);
  }

  // Phase 1: New method to get spawn position at outer edge
  private getOuterEdgeSpawnPosition(): THREE.Vector3 {
    return SpawnPositioning.generateOuterEdgePosition();
  }

  // Phase 1: Get mempool target based on fee rate
  private getMempoolTargetPosition(feeRate: number): THREE.Vector3 {
    return SpawnPositioning.getMempoolTargetPosition(feeRate);
  }
  
  private isValidPosition(position: THREE.Vector3): boolean {
    // Check for NaN or Infinity
    if (!isFinite(position.x) || !isFinite(position.y) || !isFinite(position.z)) {
      return false;
    }
    
    // Check for exact zero position (likely uninitialized)
    if (position.x === 0 && position.y === 0 && position.z === 0) {
      return false;
    }
    
    return true;
  }
  
  private redirectShipsToPlanet(blockHeight: number): void {
    // Find ships that should dock with this new planet
    for (const [txId, ship] of this.activeShips) {
      if (!this.dockingManager.isDocking(ship.id) && ship.isActive) {
        // Check if ship's target matches this block
        if (ship.data.size === blockHeight) {
          this.dockingManager.initiateDocking(ship, blockHeight);
        }
      }
    }
  }
  
  public update(deltaTime: number): void {
    // Performance optimization: batch updates
    this.updateDeltaAccumulator += deltaTime;
    
    // Update ships
    const activeShipArray = Array.from(this.activeShips.values());
    
    // Batch update ship positions
    this.trajectoryManager.updateBatch(activeShipArray, deltaTime);
    
    // Update ship visuals
    this.shipPool.updateAll(deltaTime);
    
    // Update docking
    this.dockingManager.update(deltaTime);
    
    // Update planets
    this.orbitController.updateOrbits(deltaTime);
    
    // Phase 2: Handle mempool interactions for ships
    this.updateMempoolInteractions();
    
    // Phase 3: Update mining operations with ship availability
    if (this.miningOperations) {
      this.miningOperations.integrateWithShipManagement(this.activeShips);
      this.miningOperations.update(deltaTime, 1.0, this.activeShips);
    }
    
    // Check for arrived ships and handle phase transitions
    for (const [txId, ship] of this.activeShips) {
      this.updateShipPhase(txId, ship);
    }
    
    // Update performance metrics periodically
    if (this.updateDeltaAccumulator > 1.0) {
      this.updatePerformanceMetrics();
      this.updateDeltaAccumulator = 0;
    }
  }

  /**
   * Phase 2: Handle mempool interactions for all active ships
   */
  private updateMempoolInteractions(): void {
    const store = useGalaxyStore.getState();
    const mempoolNebula = store.scene.activeMempoolNebula;
    
    if (!mempoolNebula) return;
    
    for (const [txId, ship] of this.activeShips) {
      // Check if ship is approaching mempool boundary
      if (ship.trajectory.phase === 'broadcast' && 
          ship.isApproachingMempool(new THREE.Vector3(0, 0, 0), 40)) {
        // Update trajectory phase to approaching
        ship.trajectory.phase = 'approaching-mempool';
        
        // Update lifecycle status in store
        store.updateTransactionLifecycle(txId, 'approaching-mempool');
      }
      
      // Check if ship should enter mempool
      if (ship.trajectory.phase === 'approaching-mempool' && 
          mempoolNebula.isWithinBounds(ship.trajectory.currentPosition)) {
        
        // Get transaction data for fee rate
        const txState = store.blockchain.transactions.get(txId);
        if (txState) {
          ship.enterMempool(mempoolNebula, txState.feeRate);
          
          // Update state to in-mempool
          store.updateTransactionLifecycle(txId, 'in-mempool', ship.trajectory.mempoolLayerPosition);
        }
      }
    }
  }

  /**
   * Phase 2: Update individual ship phase and handle transitions
   */
  /**
   * Phase 2-3: Update individual ship phase and handle transitions
   */
  private updateShipPhase(txId: string, ship: TransactionShip): void {
    const store = useGalaxyStore.getState();
    
    // Handle different ship phases
    switch (ship.trajectory.phase) {
      case 'broadcast':
      case 'approaching-mempool':
        // Original behavior: check if trajectory completed
        if (ship.trajectory.progress >= 1.0 && !this.dockingManager.isDocking(ship.id)) {
          // Ship reached mempool destination but didn't enter properly
          this.releaseShip(txId);
        }
        break;
        
      case 'entering-mempool':
        // Ship is transitioning to orbital movement
        // This is handled in ship.updatePosition() method
        break;
        
      case 'orbiting-mempool':
        // Ship is waiting in mempool for mining selection
        // Ships in this phase are available for Phase 3 mining selection
        break;
        
      case 'selected':
        // Phase 3: Ship has been selected by miner for inclusion in block
        // Update transaction lifecycle status in store
        const txState = store.blockchain.transactions.get(txId);
        if (txState && txState.lifecycleStatus !== 'selected') {
          store.updateTransactionLifecycle(txId, 'selected');
        }
        
        // Ship is being extracted by mining tractor beam
        // The MiningCollector handles movement toward the mining operation
        break;
        
      default:
        // Fallback: if trajectory completed and no specific handling, release ship
        if (ship.trajectory.progress >= 1.0 && !this.dockingManager.isDocking(ship.id)) {
          if (this.config.enableDocking) {
            const closestPlanet = this.orbitController.getClosestPlanet(ship.mesh.position);
            if (closestPlanet) {
              this.dockingManager.initiateDocking(ship, closestPlanet.blockHeight);
            } else {
              this.releaseShip(txId);
            }
          } else {
            this.releaseShip(txId);
          }
        }
        break;
    }
  }
  
  private updatePerformanceMetrics(): void {
    const store = useGalaxyStore.getState();
    
    // Update planet positions using the store's methods (which handle Immer properly)
    const planets = this.orbitController.getAllPlanets();
    
    planets.forEach(planet => {
      const position = planet.getPosition();
      store.registerPlanetPosition(planet.blockHeight, {
        x: position.x,
        y: position.y,
        z: position.z
      });
    });
  }
  
  public setQualityLevel(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    this.config.qualityLevel = level;
    
    // Adjust trail visibility
    const enableTrails = level !== 'low';
    for (const ship of this.activeShips.values()) {
      ship.trail.mesh.visible = enableTrails && this.config.enableTrails;
    }
    
    // Adjust ship pool size
    const maxShips = {
      low: 200,
      medium: 500,
      high: 1000,
      ultra: 2000
    };
    
    this.config.maxShips = maxShips[level];
    
    // Remove excess ships if needed
    while (this.activeShips.size > this.config.maxShips) {
      this.removeOldestShip();
    }
  }
  
  public getStatus() {
    const miningStatus = this.miningOperations?.getMiningOperationsStatus() || {
      activeCollectors: 0,
      totalActiveTargets: 0,
      averageBeamUtilization: 0,
      competitionLevel: 0,
      poolPerformance: []
    };
    
    return {
      activeShips: this.activeShips.size,
      planetCount: this.orbitController.getPlanetCount(),
      dockingShips: this.dockingManager.getApproachingShipsCount(),
      poolStats: this.shipPool.getStats(),
      // Phase 3: Mining operations status
      miningOperations: miningStatus
    };
  }
  
  public dispose(): void {
    // Remove visualization event listeners
    for (const [eventName, handler] of this.eventHandlers.entries()) {
      if (eventName === 'store_unsubscribe') {
        // Call the unsubscribe function
        (handler as any)();
      } else if (eventName.startsWith('vis_')) {
        // Remove visualization event bus listeners
        const actualEventName = eventName.replace('vis_', '');
        visualizationEventBus.off(actualEventName, handler);
      }
    }
    this.eventHandlers.clear();
    
    // Release all active ships
    for (const txId of this.activeShips.keys()) {
      this.releaseShip(txId);
    }
    
    // Phase 3: Dispose mining operations
    if (this.miningOperations) {
      this.miningOperations.dispose();
      this.miningOperations = null;
    }
    
    // Dispose systems
    this.dockingManager.dispose();
    this.orbitController.dispose();
    this.shipPool.dispose();
    
    // Clear references
    this.activeShips.clear();
  }
}